/**
 * Store Management Tab
 *
 * Admin interface for managing store items - add, edit, delete, toggle active status.
 * Supports all item types including profile items (badges/frames).
 */

import React, { useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ShoppingBag,
  Plus,
  Edit,
  Trash2,
  X,
  Upload,
  Loader2,
  Tag,
  DollarSign,
  Package,
  Image as ImageIcon,
  CheckCircle,
  XCircle,
  Eye,
  EyeOff,
  Star,
  Award,
  Frame,
  User,
  ExternalLink,
} from "lucide-react";
import {
  useStoreItems,
  useCreateStoreItem,
  useUpdateStoreItem,
} from "../../hooks/useFirebaseData";
import { StoreItem, StoreCategory, RedemptionType, ProfileItemType } from "../../types";
import { dbRemove, uploadFile } from "../../config/firebase";
import { useTestMode } from "../../contexts/TestModeContext";

// Available Feather icons for badges
const BADGE_ICONS = [
  "award", "star", "zap", "shield", "crown", "diamond", "heart", "fire",
  "trophy", "medal", "gift", "sparkles", "bolt", "gem", "rocket", "target"
];

// Color presets for badges
const BADGE_COLORS = [
  { bg: "#FFD700", text: "#000000", name: "Gold" },
  { bg: "#C0C0C0", text: "#000000", name: "Silver" },
  { bg: "#CD7F32", text: "#FFFFFF", name: "Bronze" },
  { bg: "#6366f1", text: "#FFFFFF", name: "Purple" },
  { bg: "#22c55e", text: "#FFFFFF", name: "Green" },
  { bg: "#ef4444", text: "#FFFFFF", name: "Red" },
  { bg: "#3b82f6", text: "#FFFFFF", name: "Blue" },
  { bg: "#f59e0b", text: "#000000", name: "Amber" },
  { bg: "#ec4899", text: "#FFFFFF", name: "Pink" },
  { bg: "#000000", text: "#FFD700", name: "Black Gold" },
];

interface StoreItemFormData {
  name: string;
  description: string;
  category: StoreCategory;
  subcategory: string;
  gtPrice: number | null;
  marketplaceUrl: string;
  stock: number | null;
  isActive: boolean;
  isFeatured: boolean;
  redemptionType: RedemptionType;
  redemptionInstructions: string;
  sortOrder: number;
  imageURL: string;
  // Profile item specific
  profileItemType: ProfileItemType | "";
  profileItemId: string;
  profileItemData: {
    imageUrl: string;
    iconName: string;
    color: string;
    textColor: string;
  };
}

const getDefaultFormData = (item: StoreItem | null): StoreItemFormData => ({
  name: item?.name || "",
  description: item?.description || "",
  category: item?.category || "merch",
  subcategory: item?.subcategory || "",
  gtPrice: item?.gtPrice ?? 100,
  marketplaceUrl: item?.marketplaceUrl || "",
  stock: item?.stock ?? null,
  isActive: item?.isActive ?? true,
  isFeatured: item?.isFeatured ?? false,
  redemptionType: item?.redemptionType || "physical",
  redemptionInstructions: item?.redemptionInstructions || "",
  sortOrder: item?.sortOrder || 0,
  imageURL: item?.imageURL || "",
  profileItemType: item?.profileItemType || "",
  profileItemId: item?.profileItemId || "",
  profileItemData: {
    imageUrl: item?.profileItemData?.imageUrl || "",
    iconName: item?.profileItemData?.iconName || "award",
    color: item?.profileItemData?.color || "#FFD700",
    textColor: item?.profileItemData?.textColor || "#000000",
  },
});

interface StoreItemModalProps {
  item: StoreItem | null;
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: Partial<StoreItem>) => Promise<void>;
}

const StoreItemModal: React.FC<StoreItemModalProps> = ({
  item,
  isOpen,
  onClose,
  onSave,
}) => {
  const isEditing = !!item;
  const [formData, setFormData] = useState<StoreItemFormData>(getDefaultFormData(item));
  const [isUploading, setIsUploading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [activeSection, setActiveSection] = useState<"basic" | "pricing" | "profile">("basic");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const frameInputRef = useRef<HTMLInputElement>(null);

  // Reset form when item changes
  React.useEffect(() => {
    setFormData(getDefaultFormData(item));
    setActiveSection("basic");
  }, [item, isOpen]);

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>, type: "item" | "frame") => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsUploading(true);
    try {
      const timestamp = Date.now();
      const folder = type === "frame" ? "store/frames" : "store/items";
      const path = `${folder}/${timestamp}_${file.name}`;
      const downloadURL = await uploadFile(path, file);

      if (type === "frame") {
        setFormData((prev) => ({
          ...prev,
          profileItemData: { ...prev.profileItemData, imageUrl: downloadURL },
        }));
      } else {
        setFormData((prev) => ({ ...prev, imageURL: downloadURL }));
      }
    } catch (error) {
      console.error("Image upload failed:", error);
      alert("Failed to upload image");
    } finally {
      setIsUploading(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      alert("Please enter a name");
      return;
    }

    // Validation for GT price (unless it's a marketplace item)
    const isMarketplaceItem = !!formData.marketplaceUrl;
    if (!isMarketplaceItem && (formData.gtPrice === null || formData.gtPrice < 1)) {
      alert("GT price must be at least 1 (or add a marketplace URL for external items)");
      return;
    }

    // Validation for profile items
    if (formData.category === "profile") {
      if (!formData.profileItemType) {
        alert("Please select a profile item type (Badge or Frame)");
        return;
      }
      if (!formData.profileItemId.trim()) {
        alert("Please enter a Profile Item ID");
        return;
      }
      if (formData.profileItemType === "frame" && !formData.profileItemData.imageUrl) {
        alert("Please upload a frame image");
        return;
      }
    }

    setIsSaving(true);
    try {
      const saveData: Partial<StoreItem> = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        category: formData.category,
        subcategory: formData.subcategory.trim() || undefined,
        gtPrice: isMarketplaceItem ? null : formData.gtPrice,
        marketplaceUrl: formData.marketplaceUrl.trim() || undefined,
        stock: formData.stock,
        stockReserved: item?.stockReserved || 0,
        isActive: formData.isActive,
        isFeatured: formData.isFeatured,
        redemptionType: formData.redemptionType,
        redemptionInstructions: formData.redemptionInstructions.trim() || undefined,
        sortOrder: formData.sortOrder,
        imageURL: formData.imageURL,
      };

      // Add profile item data if applicable
      if (formData.category === "profile" && formData.profileItemType) {
        saveData.profileItemType = formData.profileItemType as ProfileItemType;
        saveData.profileItemId = formData.profileItemId.trim();
        saveData.profileItemData = {
          imageUrl: formData.profileItemData.imageUrl || undefined,
          iconName: formData.profileItemType === "badge" ? formData.profileItemData.iconName : undefined,
          color: formData.profileItemType === "badge" ? formData.profileItemData.color : undefined,
          textColor: formData.profileItemType === "badge" ? formData.profileItemData.textColor : undefined,
        };
      }

      await onSave(saveData);
      onClose();
    } catch (error) {
      console.error("Save failed:", error);
      alert("Failed to save item");
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  const isProfileCategory = formData.category === "profile";

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b dark:border-gray-800">
            <h2 className="text-lg font-bold">
              {isEditing ? "Edit Store Item" : "Add Store Item"}
            </h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg"
            >
              <X size={20} />
            </button>
          </div>

          {/* Section Tabs */}
          <div className="flex border-b dark:border-gray-800">
            {[
              { id: "basic", label: "Basic Info" },
              { id: "pricing", label: "Pricing & Stock" },
              ...(isProfileCategory ? [{ id: "profile", label: "Profile Item" }] : []),
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveSection(tab.id as typeof activeSection)}
                className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
                  activeSection === tab.id
                    ? "text-primary border-b-2 border-primary"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Form Content */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {activeSection === "basic" && (
              <>
                {/* Image Upload */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-600 dark:text-gray-400">
                    Item Image
                  </label>
                  <div className="flex items-center gap-4">
                    {formData.imageURL ? (
                      <img
                        src={formData.imageURL}
                        alt="Item preview"
                        className="w-20 h-20 object-cover rounded-lg"
                      />
                    ) : (
                      <div className="w-20 h-20 bg-gray-100 dark:bg-gray-800 rounded-lg flex items-center justify-center">
                        <ImageIcon size={24} className="text-gray-400" />
                      </div>
                    )}
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleImageUpload(e, "item")}
                      className="hidden"
                    />
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      disabled={isUploading}
                      className="flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg text-sm font-medium transition-colors"
                    >
                      {isUploading ? (
                        <Loader2 size={16} className="animate-spin" />
                      ) : (
                        <Upload size={16} />
                      )}
                      {isUploading ? "Uploading..." : "Upload Image"}
                    </button>
                  </div>
                </div>

                {/* Name */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-600 dark:text-gray-400">
                    Name *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, name: e.target.value }))
                    }
                    className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-primary/50 outline-none"
                    placeholder="Item name"
                  />
                </div>

                {/* Description */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-600 dark:text-gray-400">
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        description: e.target.value,
                      }))
                    }
                    className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-primary/50 outline-none resize-none"
                    rows={3}
                    placeholder="Item description"
                  />
                </div>

                {/* Category & Subcategory */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-600 dark:text-gray-400">
                      Category *
                    </label>
                    <select
                      value={formData.category}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          category: e.target.value as StoreCategory,
                          // Reset profile fields when changing category
                          profileItemType: e.target.value === "profile" ? prev.profileItemType : "",
                        }))
                      }
                      className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-primary/50 outline-none"
                    >
                      <option value="merch">Merch</option>
                      <option value="tickets">Tickets</option>
                      <option value="profile">Profile Items</option>
                      <option value="giftcard">Gift Card</option>
                    </select>
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-600 dark:text-gray-400">
                      Subcategory
                    </label>
                    <input
                      type="text"
                      value={formData.subcategory}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          subcategory: e.target.value,
                        }))
                      }
                      className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-primary/50 outline-none"
                      placeholder="e.g., T-Shirts, Hats"
                    />
                  </div>
                </div>

                {/* Redemption Type & Instructions */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-600 dark:text-gray-400">
                    Redemption Type
                  </label>
                  <select
                    value={formData.redemptionType}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        redemptionType: e.target.value as RedemptionType,
                      }))
                    }
                    className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-primary/50 outline-none"
                  >
                    <option value="physical">Physical (Shipping Required)</option>
                    <option value="digital">Digital (Auto-fulfilled)</option>
                    <option value="code">Code (Manual or Auto Code)</option>
                  </select>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-600 dark:text-gray-400">
                    Redemption Instructions
                  </label>
                  <textarea
                    value={formData.redemptionInstructions}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        redemptionInstructions: e.target.value,
                      }))
                    }
                    className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-primary/50 outline-none resize-none"
                    rows={2}
                    placeholder="Instructions shown to user after purchase"
                  />
                </div>

                {/* Featured & Status */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-600 dark:text-gray-400">
                      Featured
                    </label>
                    <button
                      onClick={() =>
                        setFormData((prev) => ({
                          ...prev,
                          isFeatured: !prev.isFeatured,
                        }))
                      }
                      className={`w-full px-3 py-2 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 ${
                        formData.isFeatured
                          ? "bg-amber-100 dark:bg-amber-900/30 text-amber-600"
                          : "bg-gray-100 dark:bg-gray-800 text-gray-500"
                      }`}
                    >
                      <Star size={16} fill={formData.isFeatured ? "currentColor" : "none"} />
                      {formData.isFeatured ? "Featured" : "Not Featured"}
                    </button>
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-600 dark:text-gray-400">
                      Status
                    </label>
                    <button
                      onClick={() =>
                        setFormData((prev) => ({
                          ...prev,
                          isActive: !prev.isActive,
                        }))
                      }
                      className={`w-full px-3 py-2 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 ${
                        formData.isActive
                          ? "bg-green-100 dark:bg-green-900/30 text-green-600"
                          : "bg-gray-100 dark:bg-gray-800 text-gray-500"
                      }`}
                    >
                      {formData.isActive ? (
                        <>
                          <Eye size={16} /> Active
                        </>
                      ) : (
                        <>
                          <EyeOff size={16} /> Inactive
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </>
            )}

            {activeSection === "pricing" && (
              <>
                {/* Marketplace URL */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-600 dark:text-gray-400">
                    Marketplace URL (Optional)
                  </label>
                  <div className="relative">
                    <input
                      type="url"
                      value={formData.marketplaceUrl}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          marketplaceUrl: e.target.value,
                        }))
                      }
                      className="w-full px-3 py-2 pl-9 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-primary/50 outline-none"
                      placeholder="https://marketplace.com/item"
                    />
                    <ExternalLink
                      size={16}
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                    />
                  </div>
                  <p className="text-xs text-gray-500">
                    If set, this will be an external item that redirects to the marketplace instead of using GT
                  </p>
                </div>

                {/* GT Price & Stock */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-600 dark:text-gray-400">
                      GT Price {!formData.marketplaceUrl && "*"}
                    </label>
                    <div className="relative">
                      <input
                        type="number"
                        min="1"
                        value={formData.gtPrice ?? ""}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            gtPrice: e.target.value ? parseInt(e.target.value) : null,
                          }))
                        }
                        disabled={!!formData.marketplaceUrl}
                        className="w-full px-3 py-2 pl-8 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-primary/50 outline-none disabled:opacity-50"
                        placeholder={formData.marketplaceUrl ? "N/A" : "100"}
                      />
                      <DollarSign
                        size={16}
                        className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-600 dark:text-gray-400">
                      Stock (empty = unlimited)
                    </label>
                    <input
                      type="number"
                      min="0"
                      value={formData.stock ?? ""}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          stock:
                            e.target.value === "" ? null : parseInt(e.target.value),
                        }))
                      }
                      className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-primary/50 outline-none"
                      placeholder="Unlimited"
                    />
                  </div>
                </div>

                {/* Sort Order */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-600 dark:text-gray-400">
                    Sort Order
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={formData.sortOrder}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        sortOrder: parseInt(e.target.value) || 0,
                      }))
                    }
                    className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-primary/50 outline-none"
                  />
                  <p className="text-xs text-gray-500">
                    Lower numbers appear first in the store
                  </p>
                </div>
              </>
            )}

            {activeSection === "profile" && isProfileCategory && (
              <>
                {/* Profile Item Type */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-600 dark:text-gray-400">
                    Profile Item Type *
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      onClick={() =>
                        setFormData((prev) => ({ ...prev, profileItemType: "badge" }))
                      }
                      className={`p-4 rounded-lg border-2 flex flex-col items-center gap-2 transition-colors ${
                        formData.profileItemType === "badge"
                          ? "border-primary bg-primary/10"
                          : "border-gray-200 dark:border-gray-700 hover:border-gray-300"
                      }`}
                    >
                      <Award size={32} className={formData.profileItemType === "badge" ? "text-primary" : "text-gray-400"} />
                      <span className="font-medium">Badge</span>
                      <span className="text-xs text-gray-500">Icon + colors</span>
                    </button>
                    <button
                      onClick={() =>
                        setFormData((prev) => ({ ...prev, profileItemType: "frame" }))
                      }
                      className={`p-4 rounded-lg border-2 flex flex-col items-center gap-2 transition-colors ${
                        formData.profileItemType === "frame"
                          ? "border-primary bg-primary/10"
                          : "border-gray-200 dark:border-gray-700 hover:border-gray-300"
                      }`}
                    >
                      <Frame size={32} className={formData.profileItemType === "frame" ? "text-primary" : "text-gray-400"} />
                      <span className="font-medium">Frame</span>
                      <span className="text-xs text-gray-500">Profile border</span>
                    </button>
                  </div>
                </div>

                {/* Profile Item ID */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-600 dark:text-gray-400">
                    Profile Item ID *
                  </label>
                  <input
                    type="text"
                    value={formData.profileItemId}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        profileItemId: e.target.value.toLowerCase().replace(/\s+/g, "_"),
                      }))
                    }
                    className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-primary/50 outline-none"
                    placeholder="e.g., og_badge, gold_frame"
                  />
                  <p className="text-xs text-gray-500">
                    Unique identifier used to unlock this item on user profiles
                  </p>
                </div>

                {/* Badge Configuration */}
                {formData.profileItemType === "badge" && (
                  <>
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-600 dark:text-gray-400">
                        Badge Icon
                      </label>
                      <div className="grid grid-cols-8 gap-2">
                        {BADGE_ICONS.map((iconName) => (
                          <button
                            key={iconName}
                            onClick={() =>
                              setFormData((prev) => ({
                                ...prev,
                                profileItemData: { ...prev.profileItemData, iconName },
                              }))
                            }
                            className={`p-2 rounded-lg border-2 transition-colors ${
                              formData.profileItemData.iconName === iconName
                                ? "border-primary bg-primary/10"
                                : "border-gray-200 dark:border-gray-700 hover:border-gray-300"
                            }`}
                            title={iconName}
                          >
                            <Award size={20} className="mx-auto" />
                          </button>
                        ))}
                      </div>
                      <p className="text-xs text-gray-500">
                        Selected: {formData.profileItemData.iconName}
                      </p>
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-600 dark:text-gray-400">
                        Badge Colors
                      </label>
                      <div className="grid grid-cols-5 gap-2">
                        {BADGE_COLORS.map((preset) => (
                          <button
                            key={preset.name}
                            onClick={() =>
                              setFormData((prev) => ({
                                ...prev,
                                profileItemData: {
                                  ...prev.profileItemData,
                                  color: preset.bg,
                                  textColor: preset.text,
                                },
                              }))
                            }
                            className={`p-2 rounded-lg border-2 transition-colors ${
                              formData.profileItemData.color === preset.bg
                                ? "border-primary"
                                : "border-gray-200 dark:border-gray-700"
                            }`}
                            title={preset.name}
                          >
                            <div
                              className="w-full h-6 rounded flex items-center justify-center text-xs font-bold"
                              style={{ backgroundColor: preset.bg, color: preset.text }}
                            >
                              {preset.name}
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Badge Preview */}
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-600 dark:text-gray-400">
                        Preview
                      </label>
                      <div className="flex items-center gap-3 p-4 bg-gray-100 dark:bg-gray-800 rounded-lg">
                        <div
                          className="px-3 py-1.5 rounded-full flex items-center gap-1.5 font-medium text-sm"
                          style={{
                            backgroundColor: formData.profileItemData.color,
                            color: formData.profileItemData.textColor,
                          }}
                        >
                          <Award size={14} />
                          {formData.name || "Badge Name"}
                        </div>
                      </div>
                    </div>
                  </>
                )}

                {/* Frame Configuration */}
                {formData.profileItemType === "frame" && (
                  <>
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-600 dark:text-gray-400">
                        Frame Image *
                      </label>
                      <div className="flex items-center gap-4">
                        {formData.profileItemData.imageUrl ? (
                          <div className="relative w-24 h-24">
                            <img
                              src={formData.profileItemData.imageUrl}
                              alt="Frame preview"
                              className="w-full h-full object-contain"
                            />
                          </div>
                        ) : (
                          <div className="w-24 h-24 bg-gray-100 dark:bg-gray-800 rounded-lg flex items-center justify-center border-2 border-dashed border-gray-300">
                            <Frame size={32} className="text-gray-400" />
                          </div>
                        )}
                        <input
                          ref={frameInputRef}
                          type="file"
                          accept="image/png"
                          onChange={(e) => handleImageUpload(e, "frame")}
                          className="hidden"
                        />
                        <div className="flex-1">
                          <button
                            onClick={() => frameInputRef.current?.click()}
                            disabled={isUploading}
                            className="flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg text-sm font-medium transition-colors"
                          >
                            {isUploading ? (
                              <Loader2 size={16} className="animate-spin" />
                            ) : (
                              <Upload size={16} />
                            )}
                            Upload Frame PNG
                          </button>
                          <p className="text-xs text-gray-500 mt-2">
                            Use a transparent PNG. Recommended: 200x200px with transparent center.
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Frame Preview */}
                    {formData.profileItemData.imageUrl && (
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-600 dark:text-gray-400">
                          Preview on Profile
                        </label>
                        <div className="flex justify-center p-4 bg-gray-100 dark:bg-gray-800 rounded-lg">
                          <div className="relative w-20 h-20">
                            <div className="absolute inset-0 bg-gradient-to-br from-primary to-purple-600 rounded-full" />
                            <img
                              src={formData.profileItemData.imageUrl}
                              alt="Frame"
                              className="absolute inset-0 w-full h-full object-contain"
                            />
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </>
            )}
          </div>

          {/* Actions */}
          <div className="flex gap-3 p-4 border-t dark:border-gray-800">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2.5 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg font-medium transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={isSaving || isUploading}
              className="flex-1 px-4 py-2.5 bg-primary hover:bg-primary-dark text-black font-medium rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {isSaving ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  Saving...
                </>
              ) : (
                <>Save Item</>
              )}
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

// Store Item Row
const StoreItemRow: React.FC<{
  item: StoreItem;
  onEdit: () => void;
  onDelete: () => void;
  onToggleActive: () => void;
}> = ({ item, onEdit, onDelete, onToggleActive }) => {
  const getCategoryColor = (category: string) => {
    switch (category) {
      case "merch":
        return "bg-blue-100 text-blue-600 dark:bg-blue-900/30";
      case "tickets":
        return "bg-purple-100 text-purple-600 dark:bg-purple-900/30";
      case "profile":
        return "bg-green-100 text-green-600 dark:bg-green-900/30";
      case "giftcard":
        return "bg-yellow-100 text-yellow-600 dark:bg-yellow-900/30";
      default:
        return "bg-gray-100 text-gray-600 dark:bg-gray-800";
    }
  };

  const isMarketplaceItem = !!item.marketplaceUrl;

  return (
    <div className="flex items-center gap-4 p-4 bg-surface-light dark:bg-surface-dark rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">
      {/* Image */}
      {item.imageURL ? (
        <img
          src={item.imageURL}
          alt={item.name}
          className="w-12 h-12 object-cover rounded-lg"
        />
      ) : (
        <div className="w-12 h-12 bg-gray-200 dark:bg-gray-700 rounded-lg flex items-center justify-center">
          <Package size={20} className="text-gray-400" />
        </div>
      )}

      {/* Info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <h4 className="font-semibold truncate">{item.name}</h4>
          <span
            className={`px-2 py-0.5 text-xs font-medium rounded-full ${getCategoryColor(
              item.category
            )}`}
          >
            {item.category}
          </span>
          {item.profileItemType && (
            <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-indigo-100 text-indigo-600 dark:bg-indigo-900/30">
              {item.profileItemType}
            </span>
          )}
          {item.isFeatured && (
            <Star size={14} className="text-amber-500" fill="currentColor" />
          )}
          {!item.isActive && (
            <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-400">
              Inactive
            </span>
          )}
          {isMarketplaceItem && (
            <ExternalLink size={14} className="text-gray-400" />
          )}
        </div>
        <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
          {item.description || "No description"}
        </p>
      </div>

      {/* Price & Stock */}
      <div className="text-right">
        {isMarketplaceItem ? (
          <p className="font-medium text-gray-500">Marketplace</p>
        ) : (
          <p className="font-bold text-primary">{(item.gtPrice ?? 0).toLocaleString()} GT</p>
        )}
        <p className="text-xs text-gray-500">
          {item.stock === null ? "Unlimited" : `${item.stock - (item.stockReserved || 0)} left`}
        </p>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-1">
        <button
          onClick={onToggleActive}
          className={`p-2 rounded-lg transition-colors ${
            item.isActive
              ? "hover:bg-yellow-100 dark:hover:bg-yellow-900/30 text-yellow-600"
              : "hover:bg-green-100 dark:hover:bg-green-900/30 text-green-600"
          }`}
          title={item.isActive ? "Deactivate" : "Activate"}
        >
          {item.isActive ? <EyeOff size={18} /> : <Eye size={18} />}
        </button>
        <button
          onClick={onEdit}
          className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
          title="Edit"
        >
          <Edit size={18} />
        </button>
        <button
          onClick={onDelete}
          className="p-2 hover:bg-red-100 dark:hover:bg-red-900/30 text-red-500 rounded-lg transition-colors"
          title="Delete"
        >
          <Trash2 size={18} />
        </button>
      </div>
    </div>
  );
};

// Main Tab Component
export const StoreManagementTab: React.FC = () => {
  const { getPath } = useTestMode();
  const { data: storeItems, isLoading } = useStoreItems();
  const createItem = useCreateStoreItem();
  const updateItem = useUpdateStoreItem();

  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState<StoreItem | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState<string>("all");

  // Filter items
  const filteredItems = (storeItems || [])
    .filter((item) => {
      const matchesSearch =
        item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.description?.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesCategory =
        categoryFilter === "all" || item.category === categoryFilter;
      return matchesSearch && matchesCategory;
    })
    .sort((a, b) => a.sortOrder - b.sortOrder);

  // Stats
  const stats = {
    total: storeItems?.length || 0,
    active: storeItems?.filter((i) => i.isActive).length || 0,
    featured: storeItems?.filter((i) => i.isFeatured).length || 0,
    profileItems: storeItems?.filter((i) => i.category === "profile").length || 0,
    lowStock: storeItems?.filter((i) => i.stock !== null && i.stock - (i.stockReserved || 0) < 10).length || 0,
  };

  const handleSave = async (data: Partial<StoreItem>) => {
    if (editingItem) {
      await updateItem.mutateAsync({
        itemId: editingItem.itemId,
        data,
      });
    } else {
      await createItem.mutateAsync(data as Omit<StoreItem, 'itemId' | 'createdAt' | 'updatedAt'>);
    }
  };

  const handleDelete = async (itemId: string) => {
    if (!window.confirm("Are you sure you want to delete this item?")) return;

    try {
      await dbRemove(getPath(`store/items/${itemId}`));
    } catch (error) {
      console.error("Delete failed:", error);
      alert("Failed to delete item");
    }
  };

  const handleToggleActive = async (item: StoreItem) => {
    await updateItem.mutateAsync({
      itemId: item.itemId,
      data: { isActive: !item.isActive },
    });
  };

  const handleEdit = (item: StoreItem) => {
    setEditingItem(item);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingItem(null);
  };

  return (
    <motion.div
      key="store"
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      className="space-y-4"
    >
      {/* Stats */}
      <div className="card p-5">
        <h3 className="text-lg font-bold mb-4">Store Statistics</h3>
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
          <div className="bg-surface-light dark:bg-gray-800 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <Package size={16} className="text-primary" />
              <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark">
                Total Items
              </p>
            </div>
            <p className="text-2xl font-bold">{stats.total}</p>
          </div>
          <div className="bg-surface-light dark:bg-gray-800 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle size={16} className="text-success" />
              <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark">
                Active Items
              </p>
            </div>
            <p className="text-2xl font-bold text-success">{stats.active}</p>
          </div>
          <div className="bg-surface-light dark:bg-gray-800 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <Star size={16} className="text-amber-500" />
              <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark">
                Featured
              </p>
            </div>
            <p className="text-2xl font-bold text-amber-500">{stats.featured}</p>
          </div>
          <div className="bg-surface-light dark:bg-gray-800 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <User size={16} className="text-indigo-500" />
              <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark">
                Profile Items
              </p>
            </div>
            <p className="text-2xl font-bold text-indigo-500">{stats.profileItems}</p>
          </div>
          <div className="bg-surface-light dark:bg-gray-800 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <Tag size={16} className="text-warning" />
              <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark">
                Low Stock
              </p>
            </div>
            <p className="text-2xl font-bold text-warning">{stats.lowStock}</p>
          </div>
        </div>
      </div>

      {/* Items Management */}
      <div className="card p-5">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
          <h3 className="text-lg font-bold">Store Items</h3>
          <div className="flex items-center gap-3">
            {/* Search */}
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search items..."
              className="px-3 py-2 bg-surface-light dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm outline-none focus:ring-2 focus:ring-primary/50"
            />
            {/* Category Filter */}
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="px-3 py-2 bg-surface-light dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm outline-none focus:ring-2 focus:ring-primary/50"
            >
              <option value="all">All Categories</option>
              <option value="merch">Merch</option>
              <option value="tickets">Tickets</option>
              <option value="profile">Profile Items</option>
              <option value="giftcard">Gift Card</option>
            </select>
            {/* Add Button */}
            <button
              onClick={() => {
                setEditingItem(null);
                setShowModal(true);
              }}
              className="flex items-center gap-2 px-4 py-2 bg-primary hover:bg-primary-dark text-black font-medium rounded-lg transition-colors"
            >
              <Plus size={18} />
              Add Item
            </button>
          </div>
        </div>

        {/* Items List */}
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 size={24} className="animate-spin text-primary" />
          </div>
        ) : filteredItems.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <ShoppingBag size={48} className="mx-auto mb-4 opacity-20" />
            <p>No store items found</p>
          </div>
        ) : (
          <div className="space-y-2">
            {filteredItems.map((item) => (
              <StoreItemRow
                key={item.itemId}
                item={item}
                onEdit={() => handleEdit(item)}
                onDelete={() => handleDelete(item.itemId)}
                onToggleActive={() => handleToggleActive(item)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Modal */}
      <StoreItemModal
        item={editingItem}
        isOpen={showModal}
        onClose={handleCloseModal}
        onSave={handleSave}
      />
    </motion.div>
  );
};

export default StoreManagementTab;
